// Rendering a contract email template -- the browser half.
//
// Mirrors supabase/functions/_shared/contractEmailTemplate.ts, which renders
// what is actually sent. This one renders the preview in Contract settings and
// on the Review & send screen. If the two disagree, the preview lies about what
// a counterparty receives, so scripts/test-contract-email.mjs runs both against
// the same fixtures on every commit. Change one, change the other.
//
// See the .ts file for the rules (closed token list, no {{signing_link}},
// paragraphs with an unresolvable token are dropped whole).

import { MERGE_TOKENS, resolveMerge } from './contractFields';

export const DOCUMENT_TOKENS = Object.keys(MERGE_TOKENS);

// Only meaningful in an email: a document has no "deadline to sign" or sender.
export const EMAIL_ONLY_TOKENS = {
  '{{signature_deadline}}': 'The date they need to sign by',
  '{{sender_name}}':        'Your name, as the person sending',
};

export const EMAIL_TOKENS = [...DOCUMENT_TOKENS, ...Object.keys(EMAIL_ONLY_TOKENS)];
const EMAIL_TOKEN_SET = new Set(EMAIL_TOKENS);

// The document hints describe "the signer this field belongs to"; in an email
// that person is simply whoever the email is going to.
export const EMAIL_TOKEN_HINTS = {
  ...MERGE_TOKENS,
  '{{signer_name}}':  'The name of the person receiving the email',
  '{{signer_email}}': 'Their email address',
  '{{signer_title}}': 'Their job title',
  '{{today}}':        'The date the email is sent',
  ...EMAIL_ONLY_TOKENS,
};

// Grouped BY WHAT EACH TOKEN IS ABOUT, and under the same headings the
// document bank uses, so the vocabulary is learned once rather than per screen.
// "Who it's with" holds a single token here because an email has only the one;
// a thin group is a smaller cost than the same idea filed under two names.
export const EMAIL_TOKEN_GROUPS = [
  { label: 'The contract',
    tokens: ['{{contract_title}}', '{{contract_number}}', '{{effective_date}}', '{{end_date}}'] },
  { label: 'Who it\u2019s with',
    tokens: ['{{counterparty_name}}', '{{counterparty_address}}',
             '{{counterparty_address_1}}', '{{counterparty_address_2}}',
             '{{counterparty_city}}', '{{counterparty_state}}', '{{counterparty_zip}}'] },
  { label: 'Your organization',
    tokens: ['{{org_name}}', '{{org_address}}',
             '{{org_address_1}}', '{{org_address_2}}',
             '{{org_city}}', '{{org_state}}', '{{org_zip}}'] },
  { label: 'The person signing',
    tokens: ['{{signer_name}}', '{{signer_email}}', '{{signer_title}}'] },
  { label: 'Sending', tokens: ['{{signature_deadline}}', '{{sender_name}}', '{{today}}'] },
];

// Keep in step with the .ts file and the seed in 20260915_contract_email_templates.sql.
export const DEFAULT_EMAIL_TEMPLATE = {
  name: 'Signature request',
  subject: '{{org_name}}: {{contract_title}} — signature requested',
  body: 'Hi {{signer_name}},\n\n{{org_name}} has sent you {{contract_title}} ({{contract_number}}) to review and sign.\n\nPlease sign by {{signature_deadline}}.',
};

const TOKEN_RE = /\{\{[^{}]*\}\}/g;

/** Every {{token}} in the text that TimeKeep does not recognize, once each. */
export function findUnknownTokens(text) {
  const out = [];
  for (const tok of String(text ?? '').match(TOKEN_RE) ?? []) {
    if (!EMAIL_TOKEN_SET.has(tok) && !out.includes(tok)) out.push(tok);
  }
  return out;
}

export function resolveEmailToken(token, ctx) {
  if (token === '{{signature_deadline}}') return ctx.deadlineLabel || null;
  if (token === '{{sender_name}}') return ctx.senderName || null;
  return resolveMerge(token, ctx);
}

function fill(text, ctx) {
  let missing = false;
  const out = String(text ?? '').replace(TOKEN_RE, (tok) => {
    if (!EMAIL_TOKEN_SET.has(tok)) return tok;
    const v = resolveEmailToken(tok, ctx);
    if (v == null || String(v).trim() === '') { missing = true; return ''; }
    return String(v);
  });
  return { text: out, missing };
}

export function renderEmail(tpl, ctx) {
  const subject = fill(tpl.subject, ctx).text.replace(/\s+/g, ' ').trim();
  const paragraphs = String(tpl.body ?? '')
    .replace(/\r\n?/g, '\n')
    .split(/\n[ \t]*\n/)
    .map((p) => fill(p, ctx))
    .filter((r) => !r.missing)
    .map((r) => r.text.split('\n').map((line) => line.trim()).join('\n').trim())
    .filter((p) => p.length > 0);
  return { subject, paragraphs };
}

/** "September 29, 2026" -- the same format send-contract puts in the email. */
export function formatDeadline(date) {
  if (!date) return null;
  return new Date(date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

/** A deadline `days` from now, or null for none. */
export function deadlineFromDays(days) {
  const n = Number(days);
  if (!Number.isFinite(n) || n <= 0) return null;
  const d = new Date();
  d.setDate(d.getDate() + Math.round(n));
  return d;
}

/**
 * Obviously fake data for previewing a template outside any real contract.
 * Never a real client or person -- this preview is seen by every org.
 */
export function sampleEmailContext({ orgName, senderName, deadlineDays = 14 } = {}) {
  return {
    contract: {
      title: 'Master Services Agreement',
      contract_number: 'CON-2026-0042',
      effective_date: null,
      effective_on_signature: true,
      end_date: null,
    },
    org: { name: orgName || 'Your organization' },
    signer: { name: 'Jane Smith', email: 'jane@example.com', title: 'Director of Operations' },
    counterpartyLabel: 'Acme Corp',
    deadlineLabel: formatDeadline(deadlineFromDays(deadlineDays)),
    senderName: senderName || 'You',
  };
}

/** Put `token` at the caret of an input/textarea, returning the new value and caret. */
export function insertAtCaret(value, el, token) {
  const v = String(value ?? '');
  const start = el && typeof el.selectionStart === 'number' ? el.selectionStart : v.length;
  const end = el && typeof el.selectionEnd === 'number' ? el.selectionEnd : start;
  return { value: v.slice(0, start) + token + v.slice(end), caret: start + token.length };
}
